import { createFileRoute } from '@tanstack/react-router';
import { CallsPanel } from '@/components/calls/CallsPanel';
import {
  CALLS_VIEWS,
  DEFAULT_CALLS_VIEW,
  type CallsView,
} from '@/components/calls/callsViews';
import { Flush } from '@/contexts/fullbleed';

type CallLogsSearch = {
  // Which section is open. Absent is Action needed, the default — so the URL
  // stays clean on the landing view and a link to the log is explicit
  // (`?view=calls`). Still in the URL so a "call to return was assigned to
  // you" notification can point at a specific section.
  view?: CallsView;
  /**
   * Filters a report card hands over with "View calls", so the log opens on
   * the calls behind the number. Read once, when the page opens.
   */
  from?: string;
  to?: string;
  direction?: 'inbound' | 'outbound';
  status?: string;
};

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const STATUSES = new Set(['MISSED_ANY', 'Answered', 'Missed', 'Rejected', 'Voicemail', 'Transferred', 'AI Handled', 'Ringing']);

// Call Logs is its own top-level workspace (a sidebar destination), separate
// from the Inbox messages surface.
export const Route = createFileRoute('/_dashboard/dashboard/calllogs')({
  validateSearch: (search: Record<string, unknown>): CallLogsSearch => {
    const view = CALLS_VIEWS.find((candidate) => candidate === search.view);
    // The default is omitted rather than written out, so `?view=action-needed`
    // and a bare `/calllogs` are one URL rather than two that look different.
    const out: CallLogsSearch = view && view !== DEFAULT_CALLS_VIEW ? { view } : {};
    if (typeof search.from === 'string' && DAY.test(search.from)) out.from = search.from;
    if (typeof search.to === 'string' && DAY.test(search.to)) out.to = search.to;
    if (search.direction === 'inbound' || search.direction === 'outbound') out.direction = search.direction;
    if (typeof search.status === 'string' && STATUSES.has(search.status)) out.status = search.status;
    return out;
  },
  component: CallLogsPage,
});

// Flush, not a hand-sized box: the grid has to fill exactly the card the shell
// gives it, so the pagination footer sits on screen. The old
// `h-[calc(100vh-64px)] -m-6` guessed that height and ignored the card's margin,
// the banners and the call bar above it — the page ran past the fold and the
// footer was only reachable by scrolling the whole page.
function CallLogsPage() {
  const { view, from, to, direction, status } = Route.useSearch();
  const navigate = Route.useNavigate();
  return (
    <>
      <Flush />
      <CallsPanel
        view={view ?? DEFAULT_CALLS_VIEW}
        initialFilters={{ from, to, direction, status }}
        onViewChange={(next) =>
          void navigate({
            search: next === DEFAULT_CALLS_VIEW ? {} : { view: next },
            replace: true,
          })
        }
      />
    </>
  );
}
